/** Server-finished summary facts and independently checked sidebar sources; no account/history policy in React. */
import { shopifyComponentCopy } from '@testfeed/product-language'
import type { StudyBuilderSummary, StudyBuilderSidebar } from '@testfeed/shopify-contracts'
import type { StudyBuilderSidebarRead } from '../../hooks/study-builder/useStudyBuilderSidebarQuery'
import { SkeletonBodyText } from '../shims/SkeletonBodyTextShim'
import { RequestFailureBanner } from '../RequestFailureBanner'
import { ProductPhotoDeck } from '../insights/cards/ProductImageDeck'
import { CreditUsageBar } from './CreditUsageBar'
import { BlockHeading } from './SectionFrame'

const copy = shopifyComponentCopy.studySummarySidebar

export function SummaryProduct({ product }: { product: StudyBuilderSummary['product'] }) {
  // The Worker sends a note for an empty product; a client block requires its matching facts.
  if (!product) throw new Error('STUDY_BUILDER_SUMMARY_PRODUCT_MISSING')
  return (
    <s-stack direction="inline" gap="small-100" alignItems="start">
      {/* The same framed single photo as the insight photo decks, so a white-background photo still has a visible edge. */}
      <ProductPhotoDeck
        photos={[{ id: product.title, label: product.title, imageUrl: product.imageUrl }]}
        interactive={false}
      />
      <s-stack direction="block" gap="small-200">
        <s-text type="strong">{product.title}</s-text>
        {product.priceLabel ? <s-text>{product.priceLabel}</s-text> : null}
        {product.variantLine ? <s-text color="subdued">{product.variantLine}</s-text> : null}
      </s-stack>
    </s-stack>
  )
}

export function SummaryTesting({ testing }: { testing: StudyBuilderSummary['testing'] }) {
  return (
    <s-stack direction="block" gap="small-300">
      <BlockHeading size="large">{copy.testingHeading}</BlockHeading>
      <s-stack direction="inline" gap="small-200" alignItems="center">
        {testing.badges.map((badge) => (
          <s-badge key={badge.label} tone={badge.tone}>
            {badge.label}
          </s-badge>
        ))}
        <s-text color="subdued">{testing.versionLine}</s-text>
      </s-stack>
      {testing.changingFieldsLine ? <s-text color="subdued">{testing.changingFieldsLine}</s-text> : null}
      {testing.questionsLine ? <s-text color="subdued">{testing.questionsLine}</s-text> : null}
    </s-stack>
  )
}

export function SummaryAudience({ audience }: { audience: StudyBuilderSummary['audience'] }) {
  return (
    <s-stack direction="block" gap="small-300">
      <BlockHeading size="large">{copy.audienceHeading}</BlockHeading>
      <s-text>{audience.primary}</s-text>
      {audience.secondary ? <s-text color="subdued">{audience.secondary}</s-text> : null}
    </s-stack>
  )
}

/** Finished source states are server-owned; Query alone supplies loading and transport failure. */
export function SummaryShoppers({
  shoppers,
  sidebar,
  offlineMessage,
}: {
  shoppers: StudyBuilderSummary['shoppers']
  sidebar: StudyBuilderSidebarRead
  offlineMessage: string
}) {
  const presentation: StudyBuilderSidebar | undefined = sidebar.data
  let source = <SkeletonBodyText lines={3} />
  if (sidebar.isError) {
    source = <RequestFailureBanner message={offlineMessage} onRetry={sidebar.refetch} pending={sidebar.isFetching} />
  } else if (presentation) {
    const { credits, timing } = presentation
    source = (
      <>
        {timing.line ? <s-text color="subdued">{timing.line}</s-text> : null}
        {credits.line ? <s-text color="subdued">{credits.line}</s-text> : null}
        {credits.bar ? <CreditUsageBar bar={credits.bar} /> : null}
        {credits.state === 'unavailable' || timing.state === 'unavailable' ? (
          <s-button variant="tertiary" disabled={sidebar.isFetching} onClick={() => void sidebar.refetch()}>
            {shopifyComponentCopy.studySetupCard.tryAgain026}
          </s-button>
        ) : null}
      </>
    )
  }
  return (
    <s-stack direction="block" gap="small-300">
      <BlockHeading size="large">{copy.shoppersHeading}</BlockHeading>
      <s-text color="subdued">{shoppers.line}</s-text>
      {source}
    </s-stack>
  )
}
